import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DefaultMaterialsService } from "../../../generated/cadendar-rpc/cadendar/defaultmaterials/v1/default_materials_pb";
import type { DefaultMaterialsByActKindSettings } from "cadendar-shared";
import {
  cadendarRpcTransport,
  shouldRetryCadendarRpcRequest,
} from "../../main/components/cadendarRpc";
import { useOwnerState } from "../../users/hooks/useOwner";
import { settingsFromRpc, settingsToRpc } from "./defaultMaterialsRpc";

export { getDefaultMaterialsErrorMessage } from "./defaultMaterialsRpc";

const defaultMaterialsClient = createClient(
  DefaultMaterialsService,
  cadendarRpcTransport,
);

function defaultMaterialsQueryKey(ownerId: string | null) {
  return ["cadendar-rpc", "default-materials", ownerId];
}

export const useDefaultMaterialsByActKind = () => {
  const ownerState = useOwnerState();
  const ownerId = ownerState.owner;
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryKey: defaultMaterialsQueryKey(ownerId),
    enabled: ownerId !== null,
    refetchOnWindowFocus: false,
    retry: shouldRetryCadendarRpcRequest,
    queryFn: async () => {
      if (!ownerId) {
        throw new Error("Praticien sélectionné introuvable.");
      }
      const response = await defaultMaterialsClient.getSettings({ ownerId });
      return settingsFromRpc(response.settings);
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (input: {
      ownerId: string;
      settings: DefaultMaterialsByActKindSettings;
    }) => {
      const response = await defaultMaterialsClient.saveSettings({
        ownerId: input.ownerId,
        settings: settingsToRpc(input.settings),
      });
      return settingsFromRpc(response.settings);
    },
    onSuccess: async (_, input) => {
      await queryClient.invalidateQueries({
        queryKey: defaultMaterialsQueryKey(input.ownerId),
      });
    },
  });

  const saveSettings = async (settings: DefaultMaterialsByActKindSettings) => {
    if (!ownerId || !settingsQuery.data) {
      throw new Error("Praticien sélectionné introuvable.");
    }
    return saveMutation.mutateAsync({ ownerId, settings });
  };

  const noOwnerError =
    !ownerState.isLoading && !ownerId
      ? new Error("Praticien sélectionné introuvable.")
      : null;

  return {
    ownerId,
    settings: settingsQuery.data,
    isLoading:
      ownerState.isLoading || (ownerId !== null && settingsQuery.isPending),
    error: ownerState.error ?? noOwnerError ?? settingsQuery.error,
    saveSettings,
    isSaving: saveMutation.isPending,
    saveError: saveMutation.error,
  };
};
